import React, { useEffect, useState } from "react";
import {
  NavLink,
  Outlet,
  useParams,
  Link,
  useNavigate,
} from "react-router-dom";
import { Menu, X, ArrowLeft, Instagram } from "lucide-react";
import { DEPARTMENTS } from "@/lib/departments";

const TABS = [
  { to: "", label: "Home", end: true },
  { to: "team", label: "Our Team" },
  { to: "clients", label: "Our Clients" },
  { to: "services", label: "Services" },
  { to: "contact", label: "Contact" },
];

export default function DepartmentLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dept = DEPARTMENTS[slug];
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!dept) navigate("/", { replace: true });
    else document.documentElement.style.setProperty("--csg-accent", dept.accent);
  }, [dept, navigate]);

  if (!dept) return null;
  const accent = dept.accent;

  return (
    <div
      className="min-h-screen bg-black text-white"
      style={{ "--csg-accent": accent }}
      data-testid={`dept-layout-${slug}`}
    >
      {/* USG-style centered header */}
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-md border-b border-white/5">
        <div className="px-5 md:px-10 py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          {/* Left nav (desktop) */}
          <nav className="hidden lg:flex items-center gap-7">
            <Link
              to={dept.sport === "commercial" ? "/" : `/sport/${dept.sport}`}
              className="flex items-center gap-2 text-white/60 hover:text-white text-[11px] tracking-[0.3em] uppercase"
              data-testid="back-to-departments"
            >
              <ArrowLeft size={12} /> Back
            </Link>
            {TABS.slice(0, 2).map((t) => (
              <NavLink
                key={t.label}
                to={t.to}
                end={t.end}
                data-testid={`nav-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `font-body text-[11px] tracking-[0.3em] uppercase transition-colors ${
                    isActive ? "text-white" : "text-white/60 hover:text-white"
                  }`
                }
                style={({ isActive }) => (isActive ? { color: accent } : {})}
              >
                {t.label}
              </NavLink>
            ))}
          </nav>

          {/* Center logo */}
          <Link
            to={`/d/${slug}`}
            className="flex items-center gap-3 justify-self-center lg:justify-self-center"
          >
            <img
              src={dept.logo}
              alt={dept.name}
              className="w-10 h-10 md:w-11 md:h-11 rounded object-contain"
            />
            <div className="leading-tight text-center">
              <div className="font-display text-base md:text-lg tracking-wider uppercase">
                <span className="text-white">COMPLETE</span>{" "}
                <span style={{ color: accent }}>SPORTS</span>{" "}
                <span className="text-white">GROUP</span>
              </div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-white/50">
                {dept.name}
              </div>
            </div>
          </Link>

          {/* Right nav (desktop) */}
          <nav className="hidden lg:flex items-center justify-end gap-7">
            {TABS.slice(2).map((t) => (
              <NavLink
                key={t.label}
                to={t.to}
                end={t.end}
                data-testid={`nav-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `font-body text-[11px] tracking-[0.3em] uppercase transition-colors ${
                    isActive ? "text-white" : "text-white/60 hover:text-white"
                  }`
                }
                style={({ isActive }) => (isActive ? { color: accent } : {})}
              >
                {t.label}
              </NavLink>
            ))}
            {dept.instagram && (
              <a
                href={dept.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-instagram"
                aria-label={`${dept.name} Instagram`}
                className="text-white/60 hover:text-white transition-colors"
              >
                <Instagram size={16} />
              </a>
            )}
          </nav>

          {/* Mobile burger */}
          <div className="lg:hidden col-start-3 justify-self-end">
            <button
              onClick={() => setOpen(true)}
              className="text-white/80 hover:text-white"
              data-testid="open-mobile-menu"
              aria-label="Menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col p-8" data-testid="mobile-menu">
          <div className="flex justify-end">
            <button onClick={() => setOpen(false)} className="text-white" aria-label="Close">
              <X size={28} />
            </button>
          </div>
          <nav className="mt-10 flex flex-col gap-7">
            <Link
              to={dept.sport === "commercial" ? "/" : `/sport/${dept.sport}`}
              onClick={() => setOpen(false)}
              className="font-body text-sm tracking-[0.3em] uppercase text-white/60"
            >
              ← Back to {dept.sport === "commercial" ? "home" : dept.sport}
            </Link>
            {TABS.map((t) => (
              <NavLink
                key={t.label}
                to={t.to}
                end={t.end}
                onClick={() => setOpen(false)}
                className="font-display text-5xl uppercase text-white"
                style={({ isActive }) => (isActive ? { color: accent } : {})}
              >
                {t.label}
              </NavLink>
            ))}
            {dept.instagram && (
              <a
                href={dept.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-instagram"
                className="flex items-center gap-3 font-body text-sm tracking-[0.3em] uppercase text-white/60 mt-2"
              >
                <Instagram size={18} style={{ color: accent }} /> Instagram
              </a>
            )}
          </nav>
        </div>
      )}

      <main data-testid="dept-main">
        <Outlet context={{ dept }} />
      </main>

      {/* Footer */}
      <footer className="mt-32 border-t border-white/10 px-6 md:px-14 py-14">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="font-display text-2xl uppercase">
              <span className="text-white">COMPLETE</span>{" "}
              <span style={{ color: accent }}>SPORTS</span>{" "}
              <span className="text-white">GROUP</span>
            </div>
            <p className="font-body text-sm text-white/55 mt-4 max-w-xs">
              Elite athlete representation across football and cricket — men's and women's.
            </p>
            {dept.instagram && (
              <a
                href={dept.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-white transition-colors"
              >
                <Instagram size={16} style={{ color: accent }} /> Follow Us
              </a>
            )}
          </div>
          <div>
            <div className="csg-label" style={{ color: accent }}>Divisions</div>
            <ul className="mt-4 space-y-2 font-body text-sm text-white/70">
              <li><Link to="/d/mens-football" className="hover:text-white">Men's Football</Link></li>
              <li><Link to="/d/womens-football" className="hover:text-white">Women's Football</Link></li>
              <li><Link to="/d/mens-cricket" className="hover:text-white">Men's Cricket</Link></li>
              <li><Link to="/d/womens-cricket" className="hover:text-white">Women's Cricket</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/5 text-[11px] tracking-[0.3em] uppercase text-white/30 flex flex-col md:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Complete Sports Group</span>
          <span>All Rights Reserved</span>
        </div>
      </footer>
    </div>
  );
}
